import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Loader2, EyeOff, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ukDate } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRow = ({ value, onPick, size = 'h-6 w-6' }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onPick && onPick(n)}
              disabled={!onPick} aria-label={`${n} stars`}
              data-testid={onPick ? `admin-star-${n}` : undefined}
              className={onPick ? 'transition-transform hover:scale-110' : 'cursor-default'}>
        <Star className={`${size} ${n <= value ? 'fill-gold text-gold' : 'text-slate-300'}`} />
      </button>
    ))}
  </div>
);

// Admin: everything we know about one person's ratings, plus a private rating
export const StaffRatingsDialog = ({ token, member, open, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [jobId, setJobId] = useState('');
  const [stars, setStars] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const email = member?.email;

  const load = async () => {
    if (!email) return;
    try {
      const res = await axios.get(`${API}/admin/staff/${encodeURIComponent(email)}/ratings`,
        { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load ratings');
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, email]);

  const saveRating = async () => {
    setBusy('save');
    try {
      await axios.post(`${API}/admin/staff/${encodeURIComponent(email)}/ratings`,
        { job_id: jobId, stars, note }, { headers });
      toast.success('Rating saved');
      setStars(0); setNote(''); setJobId('');
      await load();
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save that');
    } finally {
      setBusy('');
    }
  };

  const toggleExcluded = async (rating) => {
    setBusy(rating.id);
    try {
      const reason = rating.excluded ? '' : (window.prompt('Why should this rating not count?') || '');
      if (!rating.excluded && !reason.trim()) { setBusy(''); return; }
      await axios.post(`${API}/admin/ratings/${rating.id}/exclude`,
        { excluded: !rating.excluded, reason }, { headers });
      await load();
      onSaved && onSaved();
    } catch (err) {
      toast.error('Could not change that');
    } finally {
      setBusy('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">Ratings for {member?.name}</DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-5" data-testid="staff-ratings-dialog">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 rounded-[10px] border border-gold bg-cream p-3">
                <p className="text-[11px] font-semibold text-slate-500">Score, last 15 rated jobs</p>
                <p className="mt-1 text-xl font-extrabold text-ink" data-testid="staff-score">
                  {data.score.stars ? `${data.score.stars.toFixed(1)} ★` : '—'}
                  <span className="ml-1 text-sm font-semibold text-slate-500">
                    ({data.score.count})
                  </span>
                </p>
              </div>
              <div className="flex-1 rounded-[10px] border border-hair bg-white p-3">
                <p className="text-[11px] font-semibold text-slate-500">Reliability</p>
                <p className="mt-1 text-xl font-extrabold text-ink" data-testid="staff-reliability">
                  {data.reliability.score === null ? '—' : `${data.reliability.score}%`}
                </p>
                <p className="text-[11px] text-slate-500">
                  {data.reliability.jobs} job{data.reliability.jobs === 1 ? '' : 's'} ·{' '}
                  {data.reliability.messaged} messaged · {data.reliability.on_time} on time ·{' '}
                  {data.reliability.problems} problem{data.reliability.problems === 1 ? '' : 's'}
                </p>
              </div>
            </div>

            {/* Add a private admin rating */}
            <div className="rounded-[10px] border border-hair bg-page p-3">
              <p className="mb-2 text-sm font-semibold text-ink">Rate them on a job</p>
              <select value={jobId} onChange={(e) => setJobId(e.target.value)}
                      className="mb-2 w-full" data-testid="admin-rating-job">
                <option value="">Choose a completed job…</option>
                {data.jobs.map((job) => (
                  <option key={job.job_id} value={job.job_id}>
                    {ukDate(job.date)} · {job.job_type} · {job.client_name || 'no client name'}
                  </option>
                ))}
              </select>
              <StarRow value={stars} onPick={setStars} />
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                        className="mt-2"
                        placeholder="Private note — staff never see this"
                        data-testid="admin-rating-note" />
              <Button size="sm" className="mt-2" disabled={!jobId || !stars || busy === 'save'}
                      onClick={saveRating} data-testid="admin-rating-save">
                {busy === 'save' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save rating
              </Button>
            </div>

            {/* Every rating */}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-ink">All ratings</p>
              {data.ratings.length === 0 && (
                <p className="text-sm text-slate-500">No ratings for this person yet.</p>
              )}
              {data.ratings.map((rating) => (
                <div key={rating.id}
                     className={`rounded-[10px] border p-3 ${rating.excluded ? 'border-hair bg-page opacity-60' : 'border-hair bg-white'}`}
                     data-testid={`rating-row-${rating.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <StarRow value={rating.stars} size="h-4 w-4" />
                      <Badge variant="secondary" className="text-[10px]">
                        {rating.source === 'customer' ? 'Customer' : 'Office'}
                      </Badge>
                      {rating.excluded && (
                        <Badge variant="outline" className="text-[10px] text-slate-500">
                          Not counted
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-500">
                        {ukDate(rating.job_date)} · {rating.job_type}
                      </span>
                      <Button size="sm" variant="ghost" disabled={busy === rating.id}
                              onClick={() => toggleExcluded(rating)}
                              data-testid={`exclude-rating-${rating.id}`}>
                        {rating.excluded ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  {rating.comment && (
                    <p className="mt-2 text-sm text-slate-700">“{rating.comment}”</p>
                  )}
                  {rating.note && (
                    <p className="mt-2 rounded-[8px] bg-cream p-2 text-sm text-slate-700">
                      <span className="text-[11px] font-semibold text-slate-500">Private note: </span>
                      {rating.note}
                    </p>
                  )}
                  {rating.excluded && rating.excluded_reason && (
                    <p className="mt-1 text-[11px] text-slate-500">
                      Not counted: {rating.excluded_reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StaffRatingsDialog;
